/**
 * ConflictDialog — shown when server returns 409 (optimistic concurrency conflict).
 *
 * Single action: "Refrescar datos" — reloads the list query and the record.
 * Styles match NotEnabledModal corporate mock patterns.
 *
 * @module components/modals/ConflictDialog
 * @see FMPV-MVWC-E003-S003 I-004
 */
import React from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '../../../Button/Button';
import { useMasterViewStore } from '../../store/MasterViewContext';

export interface ConflictDialogProps {
  onRefresh: () => void;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

export function ConflictDialog({ onRefresh, t }: ConflictDialogProps) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const isOpen = useMasterViewStore((s) => s.isConflictDialogOpen);
  const closeConflictDialog = useMasterViewStore((s) => s.closeConflictDialog);

  if (!isOpen) return null;

  const handleRefresh = () => {
    closeConflictDialog();
    onRefresh();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9000]"
      style={{ background: 'rgba(15,23,42,.45)', backdropFilter: 'blur(2px)' }}
      role="alertdialog"
      aria-modal="true"
    >
      <div
        data-testid="conflict-dialog"
        className="bg-bg-primary dark:bg-dark-bg-secondary border border-transparent dark:border-dark-border-primary flex flex-col overflow-hidden"
        style={{
          borderRadius: 12,
          boxShadow: '0 24px 64px rgba(0,0,0,.22),0 4px 16px rgba(0,0,0,.1)',
          width: 420,
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between border-b border-border-primary dark:border-dark-border-primary gap-3"
          style={{ padding: '18px 20px 14px' }}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex-shrink-0 flex items-center justify-center bg-[#fff7ed] dark:bg-[rgba(234,88,12,0.15)] text-[#ea580c] dark:text-[#fb923c]"
              style={{ width: 36, height: 36, borderRadius: 8 }}
            >
              <RefreshCw size={18} />
            </div>
            <div>
              <div className="font-bold text-content-primary dark:text-dark-content-primary leading-snug" style={{ fontSize: 14 }}>
                {tt('conflictTitle', 'Conflicto de versión')}
              </div>
              <div className="text-content-tertiary dark:text-dark-content-tertiary leading-relaxed" style={{ fontSize: 12, marginTop: 3 }}>
                {tt('conflictSubtitle', 'El registro fue modificado por otro usuario')}
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="conflict-close-button"
            onClick={handleRefresh}
            className="flex-shrink-0 flex items-center justify-center p-1 rounded-md border-0 bg-transparent text-content-secondary dark:text-dark-content-secondary cursor-pointer hover:bg-background-secondary dark:hover:bg-dark-bg-primary hover:text-content-tertiary dark:hover:text-dark-content-tertiary transition-colors leading-none"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px' }}>
          <p className="text-content-primary dark:text-dark-content-primary" style={{ fontSize: 13, lineHeight: 1.6 }}>
            {tt('conflictBody', 'Este registro fue modificado por otro usuario mientras lo editabas. Recarga los datos para ver la versión más reciente antes de continuar.')}
          </p>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 border-t border-border-primary dark:border-dark-border-primary bg-background-secondary dark:bg-dark-bg-primary"
          style={{ padding: '12px 20px' }}
        >
          <Button
            data-testid="conflict-refresh-button"
            type="default"
            size="l"
            onClick={handleRefresh}
          >
            {tt('conflictRefreshButton', 'Refrescar datos')}
          </Button>
        </div>
      </div>
    </div>
  );
}
